import React, { useEffect, useMemo, useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { toast } from 'react-toastify';
import Spinner from '../components/ui/Spinner';
import MultiSelectDropdown from '../components/ui/MultiSelectDropdown';
import { exportDriverMonthlyAttendance, getDriverMonthlyAttendance, getDrivers } from '../services/drivers';
import { useBranches } from '../context/branches';
import { USER_ROLE } from '../services/auth';
import { formatDate } from '../utils/datetime';

const AttendanceReport = () => {
  const role = localStorage.getItem(USER_ROLE);
  const showBranches = role === 'admin';

  const today = new Date();
  const [selectedDate, setSelectedDate] = useState(today);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [drivers, setDrivers] = useState([]);
  const [selectedDrivers, setSelectedDrivers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total_working_days: null, completed_working_days: null });

  let branches = [];
  if (showBranches) {
    const result = useBranches({ reload: true, params: { status: 'active', paginate: false } });
    branches = result.branches;
  }

  useEffect(() => {
    if (!showBranches) {
      fetchDrivers(0);
      return;
    }

    if (branches?.length > 0) {
      const initialBranch = branches[0]?.id || '';
      setSelectedBranch(initialBranch);
      fetchDrivers(initialBranch);
    }
  }, [showBranches, branches]);

  const fetchDrivers = async (branch) => {
    setLoading(true);
    try {
      const [err, data] = await getDrivers({
        paginate: false,
        status: 'active',
        branch: showBranches ? (branch || 0) : 0,
      });

      if (err) {
        setDrivers([]);
        setSelectedDrivers([]);
        return;
      }

      const list = data?.results || (Array.isArray(data) ? data : []);
      setDrivers(list);
      setSelectedDrivers(list.map((d) => d.id));
    } finally {
      setLoading(false);
    }
  };

  const onBranchChange = async (e) => {
    const branchId = e.target.value;
    setSelectedBranch(branchId);
    setRows([]);
    setMeta({ total_working_days: null, completed_working_days: null });
    await fetchDrivers(branchId);
  };

  const loadMonthlyReport = async () => {
    if (!selectedDrivers.length) {
      toast.error('Select at least one driver.');
      return;
    }

    setLoading(true);
    try {
      const parts = formatDate(selectedDate).split('-');
      const month = Number(parts[1]);
      const year = Number(parts[0]);

      const [err, response] = await getDriverMonthlyAttendance(selectedDrivers, month, year);
      if (err) {
        setRows([]);
        setMeta({ total_working_days: null, completed_working_days: null });
        return;
      }

      const payload = (response && typeof response === 'object' && 'data' in response)
        ? response.data
        : response;

      const reportRows = Array.isArray(payload?.report) ? payload.report : [];

      setMeta({
        total_working_days: payload?.total_working_days ?? null,
        completed_working_days: payload?.completed_working_days ?? null,
      });
      setRows(reportRows.map((item, idx) => ({
        driver_id: item?.driver_id || idx + 1,
        driver_name: item?.driver_name || '-',
        branch_name: item?.branch || '-',
        row_total_working_days: item?.total_working_days ?? null,
        present_days: Number(item?.present_days || 0),
        absent_days: Number(item?.absent_days || 0),
        halfdays: Number(item?.halfdays || 0),
        overtime: Number(item?.overtime || 0),
      })));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedDrivers.length) {
      toast.error('Select at least one driver.');
      return;
    }

    const parts = formatDate(selectedDate).split('-');
    const month = Number(parts[1]);
    const year = Number(parts[0]);
    const response = await exportDriverMonthlyAttendance({
      month,
      year,
      format: 'xlsx',
      driver_ids: selectedDrivers,
    });
    if (!response || Array.isArray(response)) return;

    const blobUrl = window.URL.createObjectURL(response);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `attendance-report-${selectedDate.getFullYear()}-${selectedDate.getMonth() + 1}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  const titleMonth = useMemo(
    () => selectedDate.toLocaleString('default', { month: 'long', year: 'numeric' }),
    [selectedDate]
  );

  return (
    <div className="p-4 w-full uppercase [&_select]:uppercase [&_input[type='text']]:uppercase [&_input:disabled]:uppercase">
      <h1 className="text-xl font-bold uppercase text-[#293686] mb-4">Attendance Report</h1>

      <div className="bg-white border rounded p-4 mb-4">
        <div className="grid md:grid-cols-4 gap-4 mb-4 items-end">
          {showBranches && (
            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">Branch</label>
              <select
                value={selectedBranch}
                onChange={onBranchChange}
                className="border rounded px-3 py-2 text-sm"
              >
                <option value="">Select Branch</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className={showBranches ? 'md:col-span-2' : 'md:col-span-3'}>
            <MultiSelectDropdown
              label="Driver"
              placeholder="Select Driver"
              options={drivers}
              selectedValues={selectedDrivers}
              onChange={setSelectedDrivers}
              getOptionLabel={(item) => (item?.name || '-').toUpperCase()}
              getOptionValue={(item) => item?.id}
              showSelectAll={true}
              selectAllLabel="Select All Drivers"
              disabled={drivers.length === 0}
            />
          </div>

          <div className="flex gap-2">
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm"
              onClick={loadMonthlyReport}
            >
              View
            </button>
            <button
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm"
              onClick={handleDownload}
            >
              Download
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-8 flex-col lg:flex-row">
        <div className="w-full lg:w-1/3 min-w-[280px] border rounded-lg p-4 bg-white">
          <Calendar
            value={selectedDate}
            onChange={setSelectedDate}
            view="year"
            minDetail="year"
            maxDetail="year"
          />
          <p className="text-xs text-gray-500 mt-2">For selected month: {titleMonth}</p>
        </div>

        <div className="flex-1 border rounded-lg p-4 bg-white">
          <h3 className="text-lg font-semibold mb-3">Monthly Report for {titleMonth}</h3>
          {loading ? (
            <Spinner />
          ) : rows.length === 0 ? (
            <div className="text-gray-600">NO MONTHLY ATTENDANCE RECORDS FOUND.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-auto mt-2 border">
                <thead>
                  <tr className="bg-gray-100 text-sm">
                    <th className="py-2 px-4 text-left border">Driver</th>
                    <th className="py-2 px-4 text-left border">Branch</th>
                    <th className="py-2 px-4 text-left border">Total Working Days</th>
                    <th className="py-2 px-4 text-left border">Completed Working Days</th>
                    <th className="py-2 px-4 text-left border">Present Days</th>
                    <th className="py-2 px-4 text-left border">Absent Days</th>
                    <th className="py-2 px-4 text-left border">Halfday Days</th>
                    <th className="py-2 px-4 text-left border">Overtime Days</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr className="border-t text-sm" key={`${row.driver_id}-${idx}`}>
                      <td className="py-2 px-4 border">{row.driver_name}</td>
                      <td className="py-2 px-4 border">{row.branch_name}</td>
                      <td className="py-2 px-4 border">{meta.total_working_days ?? row.row_total_working_days ?? '-'}</td>
                      <td className="py-2 px-4 border">{meta.completed_working_days ?? '-'}</td>
                      <td className="py-2 px-4 border">{row.present_days}</td>
                      <td className="py-2 px-4 border">{row.absent_days}</td>
                      <td className="py-2 px-4 border">{row.halfdays}</td>
                      <td className="py-2 px-4 border">{row.overtime}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceReport;
